import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { REC } from "../constants/testIds2";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, User, Briefcase, MapPin } from "@phosphor-icons/react";

const STAGES = ["applied", "screening", "interview", "offered", "hired", "rejected"];
const STAGE_STYLE = {
  applied: "bg-zinc-100 text-zinc-700 border-zinc-300",
  screening: "bg-blue-50 text-blue-800 border-blue-200",
  interview: "bg-amber-50 text-amber-800 border-amber-200",
  offered: "bg-purple-50 text-purple-800 border-purple-200",
  hired: "bg-emerald-50 text-emerald-800 border-emerald-200",
  rejected: "bg-red-50 text-red-800 border-red-200",
};

export default function Recruitment() {
  const { user, tenantId } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [openJob, setOpenJob] = useState(false);
  const [openCand, setOpenCand] = useState(false);
  const [jobForm, setJobForm] = useState({ title: "", department: "", location: "", employment_type: "Full-time", openings: 1, description: "" });
  const [candForm, setCandForm] = useState({ job_id: "", name: "", email: "", phone: "", source: "LinkedIn" });

  const load = () => {
    const params = user?.role === "super_admin" && tenantId ? { tenant_id: tenantId } : {};
    api.get("/jobs", { params }).then(({ data }) => setJobs(data));
    api.get("/candidates", { params }).then(({ data }) => setCandidates(data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tenantId, user?.role]);

  const submitJob = async () => {
    try {
      const payload = { ...jobForm, openings: Number(jobForm.openings) };
      if (user?.role === "super_admin" && tenantId) payload.tenant_id = tenantId;
      await api.post("/jobs", payload);
      toast.success("Job posted");
      setOpenJob(false); setJobForm({ title: "", department: "", location: "", employment_type: "Full-time", openings: 1, description: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitCand = async () => {
    try {
      await api.post("/candidates", candForm);
      toast.success("Candidate added");
      setOpenCand(false); setCandForm({ job_id: "", name: "", email: "", phone: "", source: "LinkedIn" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const moveStage = async (cid, stage) => {
    try { await api.patch(`/candidates/${cid}`, { stage }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-emerald-700">Talent</div>
        <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Recruitment</h1>
        <p className="text-sm text-zinc-500 mt-2">{jobs.length} open jobs · {candidates.length} candidates in the pipeline.</p>
      </div>

      <Tabs defaultValue="jobs">
        <TabsList className="bg-zinc-100 border border-zinc-200 rounded-sm">
          <TabsTrigger value="jobs" data-testid="rec-tab-jobs" className="rounded-sm">Jobs</TabsTrigger>
          <TabsTrigger value="candidates" data-testid="rec-tab-candidates" className="rounded-sm">Candidates</TabsTrigger>
        </TabsList>

        <TabsContent value="jobs" className="mt-4 space-y-4">
          <div className="flex justify-end">
            <Dialog open={openJob} onOpenChange={setOpenJob}>
              <DialogTrigger asChild>
                <Button className="btn-zenith rounded-sm" data-testid={REC.jobCreateBtn}><Plus size={16} className="mr-1.5" /> Post job</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle className="font-display text-2xl">Post a job</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2 space-y-1"><Label className="text-xs">Title *</Label><Input value={jobForm.title} onChange={(e) => setJobForm({ ...jobForm, title: e.target.value })} className="rounded-sm" data-testid="job-title-input" /></div>
                  <div className="space-y-1"><Label className="text-xs">Department</Label><Input value={jobForm.department} onChange={(e) => setJobForm({ ...jobForm, department: e.target.value })} className="rounded-sm" /></div>
                  <div className="space-y-1"><Label className="text-xs">Location</Label><Input value={jobForm.location} onChange={(e) => setJobForm({ ...jobForm, location: e.target.value })} className="rounded-sm" /></div>
                  <div className="space-y-1"><Label className="text-xs">Openings</Label><Input type="number" value={jobForm.openings} onChange={(e) => setJobForm({ ...jobForm, openings: e.target.value })} className="rounded-sm" /></div>
                  <div className="space-y-1"><Label className="text-xs">Type</Label><Input value={jobForm.employment_type} onChange={(e) => setJobForm({ ...jobForm, employment_type: e.target.value })} className="rounded-sm" /></div>
                  <div className="col-span-2 space-y-1"><Label className="text-xs">Description</Label><Textarea value={jobForm.description} onChange={(e) => setJobForm({ ...jobForm, description: e.target.value })} className="rounded-sm" /></div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setOpenJob(false)} className="rounded-sm">Cancel</Button>
                  <Button onClick={submitJob} className="btn-zenith rounded-sm" data-testid={REC.jobSaveBtn}>Publish</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid={REC.jobsList}>
            {jobs.length === 0 && <div className="col-span-full border border-zinc-200 bg-white p-8 rounded-sm text-center text-sm text-zinc-500">No jobs posted yet.</div>}
            {jobs.map((j) => (
              <div key={j.id} className="border border-zinc-200 rounded-sm p-4 bg-white">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-display text-lg font-bold">{j.title}</div>
                    <div className="text-xs text-zinc-500 mt-0.5 flex items-center gap-2 flex-wrap"><Briefcase size={12} /> {j.department || "—"} · <MapPin size={12} /> {j.location || "—"}</div>
                  </div>
                  <Badge variant="outline" className="rounded-sm bg-emerald-50 text-emerald-800 border-emerald-200">{j.status}</Badge>
                </div>
                <div className="mt-3 pt-3 border-t border-zinc-100 text-xs text-zinc-500 flex items-center justify-between">
                  <span>{j.openings} opening{j.openings > 1 ? "s" : ""}</span>
                  <span>{j.candidate_count || 0} candidate{j.candidate_count === 1 ? "" : "s"}</span>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="candidates" className="mt-4 space-y-4">
          <div className="flex justify-end">
            <Dialog open={openCand} onOpenChange={setOpenCand}>
              <DialogTrigger asChild>
                <Button className="btn-zenith rounded-sm" data-testid={REC.candidateCreateBtn}><Plus size={16} className="mr-1.5" /> Add candidate</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle className="font-display text-2xl">Add candidate</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2 space-y-1"><Label className="text-xs">Job *</Label>
                    <Select value={candForm.job_id} onValueChange={(v) => setCandForm({ ...candForm, job_id: v })}>
                      <SelectTrigger className="rounded-sm" data-testid="candidate-job-select"><SelectValue placeholder="Select job" /></SelectTrigger>
                      <SelectContent>{jobs.map((j) => <SelectItem key={j.id} value={j.id}>{j.title}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1"><Label className="text-xs">Name *</Label><Input value={candForm.name} onChange={(e) => setCandForm({ ...candForm, name: e.target.value })} className="rounded-sm" data-testid="candidate-name-input" /></div>
                  <div className="space-y-1"><Label className="text-xs">Email</Label><Input value={candForm.email} onChange={(e) => setCandForm({ ...candForm, email: e.target.value })} className="rounded-sm" data-testid="candidate-email-input" /></div>
                  <div className="space-y-1"><Label className="text-xs">Phone</Label><Input value={candForm.phone} onChange={(e) => setCandForm({ ...candForm, phone: e.target.value })} className="rounded-sm" /></div>
                  <div className="space-y-1"><Label className="text-xs">Source</Label><Input value={candForm.source} onChange={(e) => setCandForm({ ...candForm, source: e.target.value })} className="rounded-sm" /></div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setOpenCand(false)} className="rounded-sm">Cancel</Button>
                  <Button onClick={submitCand} className="btn-zenith rounded-sm" data-testid={REC.candidateSaveBtn}>Add</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          {/* Kanban */}
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid={REC.candidatesList}>
            {STAGES.map((s) => (
              <div key={s} className="bg-zinc-100 border border-zinc-200 rounded-sm min-h-[300px] p-2">
                <div className="text-xs uppercase tracking-widest font-semibold text-zinc-600 mb-2 px-1">{s} · {candidates.filter((c) => c.stage === s).length}</div>
                <div className="space-y-2">
                  {candidates.filter((c) => c.stage === s).map((c) => (
                    <div key={c.id} className="bg-white border border-zinc-200 p-3 rounded-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-emerald-100 grid place-items-center text-emerald-800 text-xs font-bold">
                          {c.name?.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{c.name}</div>
                          <div className="text-[10px] text-zinc-500 truncate">{c.job_title}</div>
                        </div>
                      </div>
                      {c.email && <div className="text-[10px] text-zinc-500 mt-1 truncate">{c.email}</div>}
                      <div className="mt-2">
                        <Select value={c.stage} onValueChange={(v) => moveStage(c.id, v)}>
                          <SelectTrigger className="h-7 text-[11px] rounded-sm" data-testid={`candidate-stage-${c.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{STAGES.map((st) => <SelectItem key={st} value={st}>{st}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
